import {
  getWinningRosterIds,
  Match,
  matchCompleteByByesOrForfeits,
} from "model/Match/Match";
import { WorldTourSet } from "model/Match/Set/WorldTour/WorldTourSet";
import {
  getDefinedRosterIds,
  getRoster,
  isForfeited,
} from "model/Roster/Roster";
import {
  getSeasonRosterPointsOverride,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";
import { nonUndefined } from "Utility";

const FORFEIT_POINTS = 5;
const FORFEIT_TRACK_POINTS = 50;
export type BdlSeasonRecord = {
  rosterId: string;
  points: number | undefined;
  roundPoints: (number | undefined)[];
  position: number | undefined;
  totalTrackScore: number | undefined;
  rosterIdsBeaten: string[];
};

export type BdlSeasonRecordWithDisplay = {
  record: BdlSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createBdlSeasonRecord = (
  rosterId: string,
  numberOfPlaydays: number
): BdlSeasonRecord => {
  return {
    rosterId,
    points: undefined,
    roundPoints: Array(numberOfPlaydays).fill(undefined),
    position: undefined,
    totalTrackScore: undefined,
    rosterIdsBeaten: [],
  };
};

export const calculateBdlSeasonRecords = (
  seasonPointsOverrides: SeasonPointsOverrides | undefined,
  rosterIds: string[],
  matchesByPlayday: Match[][],
  numberOfPlaydays: number
) => {
  const records = new Map<string, BdlSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createBdlSeasonRecord(id, numberOfPlaydays));
  });

  for (let playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (let matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      const set = match.sets[0] as WorldTourSet;

      for (let rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const rosterRecord = records.get(rosterId);
        const finalPos = match.finalPositions[rosterId];
        if (matchCompleteByByesOrForfeits(match.teams.rosters)) {
          const roster = getRoster(match.teams.rosters, rosterId);
          if (!roster || !rosterRecord) {
            continue;
          }

          const forfeiter = isForfeited(roster);
          if (forfeiter) {
            rosterRecord.roundPoints[playdayI] = 0;
          } else {
            rosterRecord.roundPoints[playdayI] = FORFEIT_POINTS;
            rosterRecord.totalTrackScore =
              (rosterRecord.totalTrackScore ?? 0) + FORFEIT_TRACK_POINTS;
          }

          continue;
        }

        if (!rosterRecord || !finalPos) {
          continue;
        }

        const overrideScore = !!seasonPointsOverrides
          ? getSeasonRosterPointsOverride(
              seasonPointsOverrides,
              match.id,
              rosterId
            )?.result
          : undefined;

        if (!!overrideScore && overrideScore !== "Qual") {
          rosterRecord.roundPoints[playdayI] = overrideScore;
          continue;
        }

        let roundPoints = 0;
        let trackScore = 0;
        if (getWinningRosterIds(match)?.includes(rosterId)) {
          roundPoints++;
          rosterRecord.rosterIdsBeaten.push(
            ...getDefinedRosterIds(match.teams.rosters).filter(
              (id) => id !== rosterId
            )
          );
        }

        for (let trackI = 0; trackI < set.tracks.length; trackI++) {
          const track = set.tracks[trackI];
          if (!!track.finalPositions && track.finalPositions[rosterId] === 1) {
            roundPoints++;
          }

          trackScore += track.scores[rosterId];
        }

        rosterRecord.roundPoints[playdayI] = roundPoints;
        rosterRecord.totalTrackScore =
          (rosterRecord.totalTrackScore ?? 0) + trackScore;
      }
    }
  }

  const recordValues = Array.from(records.values());
  recordValues.forEach((rv) => {
    const roundPointValues = nonUndefined(rv.roundPoints);
    if (roundPointValues.length > 0) {
      rv.points = roundPointValues.reduce((a, b) => a + b, 0);
    }
  });

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0 || sortedRecords[0].points === undefined) {
    return sortedRecords;
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (sortedRecords[i].points === undefined) {
      break;
    }

    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return sortedRecords;
};

const compareRecords = (a: BdlSeasonRecord, b: BdlSeasonRecord): number => {
  if (a.points !== b.points) {
    if (a.points === undefined) {
      return 1;
    }

    if (b.points === undefined) {
      return -1;
    }

    return b.points - a.points;
  }

  if (a.totalTrackScore !== b.totalTrackScore) {
    return (b.totalTrackScore ?? 0) - (a.totalTrackScore ?? 0);
  }

  if (a.rosterIdsBeaten.includes(b.rosterId)) {
    return -1;
  }

  if (b.rosterIdsBeaten.includes(a.rosterId)) {
    return 1;
  }

  return 0;
};
